import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, saveUserWorkoutPlans, saveUserSettings, updateUserProfile, getCurrentUser } from '../utils/userDataManager';
import { setUserPasscode } from '../utils/supabaseDataManager';
import { getAssessmentWeek } from '../data/assessmentWeeks';
import PasscodePrompt from '../components/PasscodePrompt';
import './Settings.css';

function Settings() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [redoAssessmentConfirm, setRedoAssessmentConfirm] = useState(false);
  const [redoAssessmentLoading, setRedoAssessmentLoading] = useState(false);
  const [showPasscodePrompt, setShowPasscodePrompt] = useState(false);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    primaryGoal: '',
    targetAreas: [],
    daysPerWeek: '',
    timePerSession: '',
    homeWorkoutTime: '',
    gymWorkoutTime: '',
    gymDays: [],
    restDays: [],
    gymAccess: [],
    equipment: []
  });
  const [profileSaved, setProfileSaved] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Load user profile
    const userProfile = await getUserProfile();
    console.log('Loaded user profile from database:', userProfile);
    setProfile(userProfile);

    // Initialize profile form with current values
    if (userProfile) {
      const parseJsonField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return [];
          }
        }
        return [];
      };

      const formData = {
        primaryGoal: userProfile.primaryGoal || userProfile.primary_goal || '',
        targetAreas: parseJsonField(userProfile.targetAreas || userProfile.target_areas),
        daysPerWeek: userProfile.daysPerWeek || userProfile.days_per_week || '',
        timePerSession: userProfile.timePerSession || userProfile.time_per_session || '',
        homeWorkoutTime: userProfile.homeWorkoutTime || userProfile.home_workout_time || 'early_morning',
        gymWorkoutTime: userProfile.gymWorkoutTime || userProfile.gym_workout_time || 'morning',
        gymDays: parseJsonField(userProfile.gymDays || userProfile.gym_days),
        restDays: parseJsonField(userProfile.restDays || userProfile.rest_days),
        gymAccess: parseJsonField(userProfile.gymAccess || userProfile.gym_access),
        equipment: parseJsonField(userProfile.equipment)
      };

      console.log('Initialized profile form with data:', formData);
      setProfileForm(formData);
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSaveProfile = async () => {
    try {
      console.log('Saving profile with data:', profileForm);
      const result = await updateUserProfile(profileForm);

      if (!result || !result.success) {
        console.error('Profile update failed:', result?.error);
        alert('Failed to update profile. Please try again.');
        return;
      }

      console.log('Profile updated successfully:', result.data);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);

      // Reload profile to reflect changes
      const updatedProfile = await getUserProfile();
      console.log('Reloaded profile:', updatedProfile);
      setProfile(updatedProfile);
      setEditingProfile(false);

      alert('Profile updated! Your next AI-generated workout will use these new preferences.');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const handleChangePasscode = () => {
    setShowPasscodePrompt(true);
  };

  const handlePasscodeSuccess = async (passcode) => {
    const username = getCurrentUser();
    const result = await setUserPasscode(username, passcode);

    if (result.success) {
      alert('Passcode changed successfully! ');
      setShowPasscodePrompt(false);
    } else {
      alert('Failed to change passcode. Please try again.');
    }
  };

  const handlePasscodeCancel = () => {
    setShowPasscodePrompt(false);
  };

  const handleRedoAssessment = async () => {
    if (!redoAssessmentConfirm) {
      setRedoAssessmentConfirm(true);
      return;
    }

    setRedoAssessmentLoading(true);

    try {
      // Get the assessment week based on user's goal and equipment
      const primaryGoal = profile.primaryGoal || profile.primary_goal;
      const parseJsonField = (field) => {
        if (!field) return [];
        if (Array.isArray(field)) return field;
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return [];
          }
        }
        return [];
      };
      const equipment = parseJsonField(profile.equipment);
      const gymDays = parseJsonField(profile.gymDays || profile.gym_days);
      const gymAccess = parseJsonField(profile.gymAccess || profile.gym_access);
      const assessmentWeek = getAssessmentWeek(primaryGoal, equipment, gymDays, gymAccess);

      // Save assessment week as current workout plan
      await saveUserWorkoutPlans(assessmentWeek);

      // Update plan metadata - mark as assessment week
      await saveUserSettings({
        lastPlanUpdate: new Date().toISOString(),
        lastPlanSummary: `Week 1: ${assessmentWeek.programName} - Reassessment for updated baseline data`,
        isAssessmentWeek: true,
        assessmentWeekNumber: 1
      });

      // Set localStorage flags
      localStorage.setItem('isAssessmentWeek', 'true');
      localStorage.setItem('assessmentWeekNumber', '1');

      // Clear workout history to start fresh assessment
      const clearHistory = window.confirm(
        'Do you want to clear your workout history before starting the assessment week? This will give you a clean slate for reassessment.'
      );

      if (clearHistory) {
        localStorage.removeItem('detailedWorkoutHistory');
      }

      alert('Assessment week activated! Your schedule has been updated with Week 1 assessment workouts.');
      navigate('/');
    } catch (error) {
      console.error('Error resetting to assessment week:', error);
      alert('Failed to activate assessment week. Please try again.');
    } finally {
      setRedoAssessmentLoading(false);
      setRedoAssessmentConfirm(false);
    }
  };

  return (
    <div className="settings-screen">
      <div className="settings-header">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
        <h1 className="settings-title">⚙️ Settings</h1>
      </div>

      {profile && (
        <div className="settings-card" style={{ marginTop: '20px' }}>
          <h2 className="section-title">👤 Fitness Profile & Preferences</h2>
          <p className="settings-description">
            Update your fitness goals, workout schedule, and preferences. The AI will use these settings to personalize your next workout program.
          </p>

          {!editingProfile ? (
            <div>
              <div className="info-card" style={{ marginBottom: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0' }}>Current Settings:</h4>
                <ul className="info-list" style={{ margin: 0 }}>
                  <li><strong>Primary Goal:</strong> {profileForm.primaryGoal}</li>
                  <li><strong>Target Areas:</strong> {profileForm.targetAreas.length > 0 ? profileForm.targetAreas.join(', ') : 'Full body'}</li>
                  <li><strong>Days Per Week:</strong> {profileForm.daysPerWeek}</li>
                  <li><strong>Session Duration:</strong> {profileForm.timePerSession}</li>
                  <li><strong>Gym Access:</strong> {profileForm.gymAccess.length > 0 ? profileForm.gymAccess.join(', ') : 'Home only'}</li>
                  {profileForm.gymDays.length > 0 && <li><strong>Gym Days:</strong> {profileForm.gymDays.join(', ')}</li>}
                  {profileForm.restDays.length > 0 && <li><strong>Rest Days:</strong> {profileForm.restDays.join(', ')}</li>}
                  <li><strong>Home Equipment:</strong> {profileForm.equipment.length > 0 ? profileForm.equipment.join(', ') : 'None'}</li>
                </ul>
              </div>

              <button
                className="save-button"
                onClick={() => setEditingProfile(true)}
                style={{ width: '100%' }}
              >
                ✏️ Edit Profile & Preferences
              </button>
            </div>
          ) : (
            <div>
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Primary Goal</label>
                <select
                  value={profileForm.primaryGoal}
                  onChange={(e) => handleProfileChange('primaryGoal', e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                >
                  <option value="strength">Build Functional Strength</option>
                  <option value="muscle">Build Muscle Mass</option>
                  <option value="toned">Get Toned & Defined</option>
                  <option value="athletic">Improve Athletic Performance</option>
                  <option value="weight_loss">Lose Weight & Get Fit</option>
                  <option value="general_fitness">General Fitness & Health</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Target Areas (select all that apply)</label>
                <div className="checkbox-grid">
                  {['arms', 'chest', 'shoulders', 'back', 'core', 'glutes', 'thighs', 'calves', 'full_body'].map(area => (
                    <label key={area} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profileForm.targetAreas.includes(area)}
                        onChange={() => handleArrayToggle('targetAreas', area)}
                      />
                      <span>{area.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Days Per Week</label>
                  <select
                    value={profileForm.daysPerWeek}
                    onChange={(e) => handleProfileChange('daysPerWeek', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                  >
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                    <option value="5">5 days</option>
                    <option value="6">6 days</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Session Duration</label>
                  <select
                    value={profileForm.timePerSession}
                    onChange={(e) => handleProfileChange('timePerSession', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                  >
                    <option value="under_30">Less than 30 min</option>
                    <option value="30_45">30-45 minutes</option>
                    <option value="45_60">45-60 minutes</option>
                    <option value="60_plus">60+ minutes</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Home Workout Time</label>
                  <select
                    value={profileForm.homeWorkoutTime}
                    onChange={(e) => handleProfileChange('homeWorkoutTime', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                  >
                    <option value="early_morning">Early Morning (5-7 AM)</option>
                    <option value="morning">Morning (7-10 AM)</option>
                    <option value="midday">Midday (10 AM-2 PM)</option>
                    <option value="afternoon">Afternoon (2-6 PM)</option>
                    <option value="evening">Evening (6-9 PM)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Gym Workout Time</label>
                  <select
                    value={profileForm.gymWorkoutTime}
                    onChange={(e) => handleProfileChange('gymWorkoutTime', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
                  >
                    <option value="early_morning">Early Morning (5-7 AM)</option>
                    <option value="morning">Morning (7-10 AM)</option>
                    <option value="midday">Midday (10 AM-2 PM)</option>
                    <option value="afternoon">Afternoon (2-6 PM)</option>
                    <option value="evening">Evening (6-9 PM)</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Gym Access</label>
                <div className="checkbox-grid">
                  {['planet_fitness', 'home_gym', 'commercial_gym', 'other_gym', 'no_gym'].map(gym => (
                    <label key={gym} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profileForm.gymAccess.includes(gym)}
                        onChange={() => handleArrayToggle('gymAccess', gym)}
                      />
                      <span>{gym.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              {profileForm.gymAccess.length > 0 && !profileForm.gymAccess.includes('no_gym') && (
                <div className="form-group" style={{ marginBottom: '15px' }}>
                  <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Preferred Gym Days</label>
                  <div className="checkbox-grid">
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                      <label key={day} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={profileForm.gymDays.includes(day)}
                          onChange={() => handleArrayToggle('gymDays', day)}
                        />
                        <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Preferred Rest Days</label>
                <div className="checkbox-grid">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <label key={day} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profileForm.restDays.includes(day)}
                        onChange={() => handleArrayToggle('restDays', day)}
                      />
                      <span>{day.charAt(0).toUpperCase() + day.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Home Equipment</label>
                <div className="checkbox-grid">
                  {['dumbbells', 'kettlebells', 'weight_bench', 'dip_station', 'resistance_bands', 'pull_up_bar', 'yoga_mat', 'peloton', 'treadmill', 'none'].map(equip => (
                    <label key={equip} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={profileForm.equipment.includes(equip)}
                        onChange={() => handleArrayToggle('equipment', equip)}
                      />
                      <span>{equip.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              {profileSaved && (
                <div className="success-message" style={{ marginBottom: '15px' }}>
                  ✅ Profile updated successfully!
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="save-button"
                  onClick={handleSaveProfile}
                  style={{ flex: 1 }}
                >
                  💾 Save Changes
                </button>
                <button
                  className="clear-button"
                  onClick={() => {
                    setEditingProfile(false);
                    loadData(); // Reset form to current values
                  }}
                  style={{ flex: 1 }}
                >
                  ❌ Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {profile && (
        <div className="settings-card" style={{ marginTop: '20px' }}>
          <h2 className="section-title">📊 Assessment Week</h2>
          <p className="settings-description">
            Restart with a Week 1 assessment to gather fresh baseline data. This is useful after a long break, when changing goals, or to track progress by comparing assessment performance over time.
          </p>

          <div className="info-card" style={{ marginBottom: '15px', backgroundColor: '#f0f8ff' }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#2563eb' }}>📝 What happens when you redo assessment?</h4>
            <ul className="info-list" style={{ margin: 0 }}>
              <li>Your current workout plan is replaced with Week 1 assessment workouts</li>
              <li>Assessment focuses on your goal: {profile.primaryGoal || profile.primary_goal}</li>
              <li>After completing the week, the AI uses your performance to generate Week 2+</li>
              <li>Optionally clear workout history for a fresh baseline</li>
              <li>Your profile settings and measurements remain unchanged</li>
            </ul>
          </div>

          {!redoAssessmentConfirm ? (
            <button
              className="save-button"
              onClick={handleRedoAssessment}
              style={{ width: '100%' }}
            >
              🔄 Redo Assessment Week
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="save-button"
                onClick={handleRedoAssessment}
                disabled={redoAssessmentLoading}
                style={{ flex: 1, backgroundColor: '#dc2626' }}
              >
                {redoAssessmentLoading ? '⏳ Activating...' : '✅ Confirm - Start Assessment'}
              </button>
              <button
                className="clear-button"
                onClick={() => setRedoAssessmentConfirm(false)}
                disabled={redoAssessmentLoading}
                style={{ flex: 1 }}
              >
                ❌ Cancel
              </button>
            </div>
          )}

          {redoAssessmentConfirm && (
            <p style={{ marginTop: '10px', color: '#dc2626', fontSize: '14px', textAlign: 'center' }}>
              ⚠️ This will replace your current workout plan. Are you sure?
            </p>
          )}
        </div>
      )}

      {profile && (
        <div className="settings-card" style={{ marginTop: '20px' }}>
          <h2 className="section-title">🔐 Security</h2>
          <p className="settings-description">
            Change your 4-digit passcode used for user authentication.
          </p>

          <button
            className="save-button"
            onClick={handleChangePasscode}
            style={{ width: '100%' }}
          >
            🔑 Change Passcode
          </button>
        </div>
      )}

      {showPasscodePrompt && (
        <PasscodePrompt
          username={getCurrentUser()}
          mode="setup"
          onSuccess={handlePasscodeSuccess}
          onCancel={handlePasscodeCancel}
        />
      )}
    </div>
  );
}

export default Settings;
